// Centralised date/time formatting for the Strategy Engine UI.
// All exact timestamps are pinned to IST (Asia/Kolkata) so a 14:32 IST value
// reads the same in every viewer's timezone — important for a trading product.

const IST = 'Asia/Kolkata';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad2 = (value) => String(value).padStart(2, '0');

// IST-pinned Intl formatter to extract numeric date parts
const istPartsFmt = new Intl.DateTimeFormat('en-US', {
  year: 'numeric', month: 'numeric', day: 'numeric', timeZone: IST,
});

// IST-pinned clock — 12-hour hh:mm:ss am/pm
const istClockFmt = new Intl.DateTimeFormat('en-IN', {
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, timeZone: IST,
});

// IST-pinned short clock — 12-hour hh:mm am/pm (no seconds, for compact tooltips)
const istShortClockFmt = new Intl.DateTimeFormat('en-IN', {
  hour: '2-digit', minute: '2-digit', hour12: true, timeZone: IST,
});

const toDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    let num = value;
    if (num > 0 && num < 1e11) num *= 1000;
    const d = new Date(num);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === 'string') {
    let str = value.trim();
    if (!str || str === 'null' || str === 'undefined') return null;

    // Handle pure numeric epoch strings like "1785397741953"
    if (/^\d+$/.test(str)) {
      const d = new Date(Number(str));
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // If string already includes timezone info (ends with Z, +HH:MM, -HH:MM)
    if (/[zZ]$/.test(str) || /[+-]\d{2}:?\d{2}$/.test(str)) {
      const d = new Date(str);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // Backend / AWS / MySQL returns UTC timestamps without offset like "2026-07-30 06:26:00" or "2026-07-30T06:26:00"
    // Replace space with 'T' and append 'Z' so JS explicitly parses it as UTC!
    let formattedStr = str.replace(' ', 'T');
    if (!/[zZ]$/.test(formattedStr) && !/[+-]\d{2}:?\d{2}$/.test(formattedStr)) {
      formattedStr += 'Z';
    }

    const d = new Date(formattedStr);
    if (!Number.isNaN(d.getTime())) {
      return d;
    }

    // Fallback if parsing with 'Z' failed
    const fallback = new Date(str);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  return null;
};

// Extract { year, month (1-12), day } from a Date using IST timezone
const getISTDateParts = (d) => {
  const parts = istPartsFmt.formatToParts(d);
  const result = {};
  for (const p of parts) {
    if (p.type === 'year') result.year = Number(p.value);
    else if (p.type === 'month') result.month = Number(p.value);
    else if (p.type === 'day') result.day = Number(p.value);
  }
  return result;
};

// Format a Date to "DD MMM YYYY" using IST timezone
const toAdminDateStr = (d) => {
  const { year, month, day } = getISTDateParts(d);
  return `${pad2(day)} ${MONTHS[month - 1]} ${year}`;
};

// ─── Exported display functions ─────────────────────────────────────

// "23 Aug 2025" or fallback
export function formatAdminDate(value, fallback = '—') {
  const d = toDate(value);
  if (!d) return fallback;
  return toAdminDateStr(d);
}

// "23 Aug 2025, 08:00:00 pm" or fallback
export function formatAdminDateTime(value, fallback = '—') {
  const d = toDate(value);
  if (!d) return fallback;
  return `${toAdminDateStr(d)}, ${istClockFmt.format(d)}`;
}

// Combine separate date ("2026-07-30") and time ("09:34:24") parts, convert to IST
export function formatAdminDateTimeFromParts(dateStr, timeStr, fallback = '—') {
  if (!dateStr && !timeStr) return fallback;
  if (!dateStr) return formatAdminDateTime(`1970-01-01T${timeStr}`);
  const dateOnly = String(dateStr).split('T')[0].split(' ')[0];
  if (!dateOnly || dateOnly === 'null' || dateOnly === 'undefined') return fallback;
  const timePart = timeStr || "00:00:00";
  return formatAdminDateTime(`${dateOnly}T${timePart}`);
}

// ─── Legacy IST wrappers (used across the codebase) ─────────────────

// { date: "23 Aug 2025", clock: "02:34:01 pm" } or null
export function formatIST(value) {
  const d = toDate(value);
  if (!d) return null;
  return { date: toAdminDateStr(d), clock: istClockFmt.format(d) };
}

// "23 Aug 2025" or "—"
export function formatISTDate(value) {
  return formatAdminDate(value);
}

// "23 Aug 2025, 02:34:01 pm" or "—"
export function formatISTDateTime(value) {
  return formatAdminDateTime(value);
}

// Formats separate date ("2026-07-30") and time ("09:34:24") UTC parts to IST
export function formatISTDateTimeFromParts(dateStr, timeStr) {
  return formatAdminDateTimeFromParts(dateStr, timeStr);
}

// Compact label for chart tooltips: "23 Aug 2025, 02:34 pm" or "—"
export function formatISTShort(value) {
  const d = toDate(value);
  if (!d) return '—';
  return `${toAdminDateStr(d)}, ${istShortClockFmt.format(d)}`;
}

// "Just now" / "5 min ago" / "3h ago" / "2d ago", else the IST date.
export function getRelativeTime(value) {
  const d = toDate(value);
  if (!d) return '—';
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return formatAdminDate(d);
}

// ─── IST-stored timestamp helpers ────────────────────────────────────
// For pages where the DB already stores timestamps in IST (not UTC).
// These append '+05:30' instead of 'Z' to bare timestamps, so the
// Intl formatters (pinned to Asia/Kolkata) display the SAME time value
// without a double-conversion shift.

const toDateAsIST = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    let num = value;
    if (num > 0 && num < 1e11) num *= 1000;
    const d = new Date(num);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === 'string') {
    let str = value.trim();
    if (!str || str === 'null' || str === 'undefined') return null;

    if (/^\d+$/.test(str)) {
      const d = new Date(Number(str));
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // If string already includes timezone info, parse as-is
    if (/[zZ]$/.test(str) || /[+-]\d{2}:?\d{2}$/.test(str)) {
      const d = new Date(str);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // DB value is already IST — append +05:30 so JS doesn't treat it as UTC
    let formattedStr = str.replace(' ', 'T');
    if (!/[zZ]$/.test(formattedStr) && !/[+-]\d{2}:?\d{2}$/.test(formattedStr)) {
      formattedStr += '+05:30';
    }

    const d = new Date(formattedStr);
    if (!Number.isNaN(d.getTime())) {
      return d;
    }

    const fallback = new Date(str);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  return null;
};

// "23 Aug 2025" — treats bare timestamp as IST (no +5:30 shift)
export function formatLocalDate(value, fallback = '—') {
  const d = toDateAsIST(value);
  if (!d) return fallback;
  return toAdminDateStr(d);
}

// "23 Aug 2025, 09:00:00 am" — treats bare timestamp as IST (no +5:30 shift)
export function formatLocalDateTime(value, fallback = '—') {
  const d = toDateAsIST(value);
  if (!d) return fallback;
  return `${toAdminDateStr(d)}, ${istClockFmt.format(d)}`;
}

// { date: "23 Aug 2025", clock: "09:00:00 am" } — treats bare timestamp as IST
export function formatLocalIST(value) {
  const d = toDateAsIST(value);
  if (!d) return null;
  return { date: toAdminDateStr(d), clock: istClockFmt.format(d) };
}
